import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api.js";
import { addDays, datesCovered, monthLabel, monthStart, today, yearOf } from "../lib/dates.js";
import { bandsForRow, spanAria } from "../lib/span.js";
import { useSession } from "../lib/session.js";
import type { Occurrence } from "../lib/types.js";

/**
 * Density is the whole challenge here.
 *
 * Each cell stacks 6px person-coloured bars, so the *thickness* of the stack
 * says how loaded a day is before any number is read. Background commitments
 * sit at 45% so a school day does not look as busy as a real one.
 *
 * Anything covering more than one day is drawn differently, and deliberately:
 * a run gets a single band laid across the days it covers rather than a bar
 * per cell. At this size a bar is four pixels of colour, and four separate
 * ones say "four things happened" where one line says "she was away all week"
 * — which is the only thing the month view is for.
 */
const MAX_BARS = 4;
/** A band is a lane of its own at the top of the cell; leave room for bars. */
const MAX_LANES = 3;
/** A 6px band plus the 2px gap between them — the bar stack's own rhythm. */
const BAND_STEP = 8;

export function Month(): React.JSX.Element {
  const { zone } = useSession();
  const navigate = useNavigate();

  const [anchor, setAnchor] = useState(() => monthStart(today(zone), zone));
  const [occurrences, setOccurrences] = useState<Occurrence[] | null>(null);

  const grid = useMemo(() => {
    const first = DateTime.fromISO(anchor, { zone });
    // Sunday-first, so the grid starts on the Sunday on or before the 1st.
    const lead = first.weekday % 7;
    const gridStart = first.minus({ days: lead });
    // Whatever it takes to cover the month — five rows for a short month that
    // starts on a Sunday, six for most, occasionally more.
    const rows = Math.ceil((lead + (first.daysInMonth ?? 30)) / 7);
    return Array.from({ length: rows * 7 }, (_, i) => gridStart.plus({ days: i }));
  }, [anchor, zone]);

  const from = grid[0]?.toISODate() ?? anchor;
  const to = addDays(grid[grid.length - 1]?.toISODate() ?? anchor, 1, zone);

  const load = useCallback(async () => {
    setOccurrences((await api.events(from, to)).occurrences);
  }, [from, to]);

  useEffect(() => {
    setOccurrences(null);
    void load();
  }, [load]);

  /** The grid cut into weeks — a band is laid across one week at a time. */
  const weeks = useMemo(() => {
    const out: string[][] = [];
    for (let i = 0; i < grid.length; i += 7) {
      out.push(grid.slice(i, i + 7).map((d) => d.toISODate() ?? ""));
    }
    return out;
  }, [grid]);

  /**
   * Runs go in bands, single days go in bars. Splitting them here rather than
   * in the cell is what lets a band keep one lane across the whole week: the
   * cell never sees a spanning event and so can never place one itself.
   */
  const { spanning, single } = useMemo(() => {
    const spans: Occurrence[] = [];
    const ones = new Map<string, Occurrence[]>();

    for (const occurrence of occurrences ?? []) {
      const dates = datesCovered(occurrence.starts_at, occurrence.ends_at, zone);
      if (dates.length > 1) {
        spans.push(occurrence);
        continue;
      }
      for (const date of dates) {
        if (!ones.has(date)) ones.set(date, []);
        ones.get(date)?.push(occurrence);
      }
    }

    return { spanning: spans, single: ones };
  }, [occurrences, zone]);

  const month = DateTime.fromISO(anchor, { zone }).month;
  const todayDate = today(zone);

  return (
    <div className="screen">
      <header className="week__head">
        <button
          type="button"
          className="arrow"
          aria-label="Previous month"
          onClick={() =>
            setAnchor(DateTime.fromISO(anchor, { zone }).minus({ months: 1 }).toISODate() ?? anchor)
          }
        >
          ←
        </button>
        <h1>
          {monthLabel(anchor, zone)} <span className="month__year">{yearOf(anchor, zone)}</span>
        </h1>
        <button
          type="button"
          className="arrow"
          aria-label="Next month"
          onClick={() =>
            setAnchor(DateTime.fromISO(anchor, { zone }).plus({ months: 1 }).toISODate() ?? anchor)
          }
        >
          →
        </button>
      </header>

      <div className="month__dow">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span key={`${d}${String(i)}`}>{d}</span>
        ))}
      </div>

      <div className="month__grid">
        {weeks.map((week) => {
          const { bands, lanes } = bandsForRow(spanning, week, zone);
          const shownLanes = Math.min(lanes, MAX_LANES);

          return (
            <div key={week[0] ?? ""} className="month__week">
              {week.map((date) => {
                const cell = DateTime.fromISO(date, { zone });
                const list = single.get(date) ?? [];
                const outside = cell.month !== month;

                return (
                  <button
                    key={date}
                    type="button"
                    className={`cell${outside ? " is-outside" : ""}${date === todayDate ? " is-today" : ""}`}
                    onClick={() => void navigate(`/day/${date}`)}
                  >
                    <span className="cell__num">{cell.day}</span>
                    {/* The bands float above the row; this reserves their
                        height so the bars below never sit under one. */}
                    <span
                      className="cell__lanes"
                      style={{ height: shownLanes * BAND_STEP }}
                      aria-hidden="true"
                    />
                    <span className="cell__bars">
                      {list.slice(0, MAX_BARS - shownLanes).map((occurrence, index) => (
                        <span
                          key={`${occurrence.occurrence_id}:${String(index)}`}
                          className="cell__bar"
                          style={{
                            background: occurrence.people[0]?.color ?? "var(--mid)",
                            opacity: occurrence.is_background ? 0.45 : 1,
                          }}
                        />
                      ))}
                    </span>
                  </button>
                );
              })}

              <div className="month__bands" aria-hidden="true">
                {bands
                  .filter((band) => band.lane < MAX_LANES)
                  .map((band) => (
                    <span
                      key={`${band.item.occurrence_id}:${week[0] ?? ""}`}
                      className={[
                        "month__band",
                        band.clippedStart ? "is-clipped-start" : "",
                        band.clippedEnd ? "is-clipped-end" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      title={spanAria(band.item, week[band.from] ?? "", zone)}
                      style={{
                        // The band owns the columns it covers, so it draws
                        // straight over the cell borders between them.
                        gridColumn: `${String(band.from + 1)} / ${String(band.to + 2)}`,
                        gridRow: band.lane + 1,
                        background: band.item.people[0]?.color ?? "var(--mid)",
                        opacity: band.item.is_background ? 0.45 : 1,
                      }}
                    />
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
